import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import PlantBackground from '../components/PlantBackground';
import './PlantsCollection.css';

/* ─── Plant Data ─── */
const plants = [
  /* Indoor */
  { id: 1, name: 'Asplenium Fern', category: 'Indoor', emoji: '🌿', desc: 'Fresh green indoor fern that adds natural beauty and improves air quality in homes and offices.', care: 'Easy', light: 'Indirect', water: 'Weekly', image: '/images/Asplenium.jpg' },

  { id: 2, name: 'Lucky Bamboo', category: 'Indoor', emoji: '🎋', desc: 'A lucky indoor plant symbolizing prosperity, positivity, and elegant home décor.', care: 'Moderate', light: 'Bright', water: 'Weekly', image: '/images/Bamboo.jpg' },

  { id: 3, name: 'Fiddle Leaf Fig', category: 'Indoor', emoji: '🍀', desc: 'A premium indoor plant with large glossy leaves perfect for modern interiors.', care: 'Easy', light: 'Low-Medium', water: '2x Week', image: '/images/fiddle.png' },

  { id: 4, name: 'Golden Pothos', category: 'Indoor', emoji: '🌱', desc: 'An easy-care air-purifying indoor plant ideal for homes, offices, and balconies.', care: 'Easy', light: 'Any', water: 'Monthly', image: '/images/Golden Pothos.jpg' },

  { id: 5, name: 'Jade Plant', category: 'Indoor', emoji: '💰', desc: 'A beautiful succulent known for attracting good luck, wealth, and positive energy.', care: 'Easy', light: 'Low-Medium', water: 'Monthly', image: '/images/jade plant.jpg' },

  { id: 6, name: 'Modern Succulent', category: 'Indoor', emoji: '🌵', desc: 'A stylish low-maintenance succulent plant perfect for modern home and office décor.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Modern succulent.jpg' },

  { id: 7, name: 'Monstera Deliciosa', category: 'Indoor', emoji: '🍃', desc: 'A trendy tropical indoor plant with iconic split leaves and stunning visual appeal.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Monstera.jpg' },

  { id: 8, name: 'Areca Palm', category: 'Indoor', emoji: '🌴', desc: 'A lush air-purifying palm that brings a refreshing tropical feel to indoor spaces.', care: 'Easy', light: 'Any', water: 'Monthly', image: '/images/palm.png' },

  { id: 9, name: 'Parlor Palm', category: 'Indoor', emoji: '🌴', desc: 'An elegant indoor palm plant ideal for living rooms, offices, and apartments.', care: 'Easy', light: 'Low-Medium', water: 'Monthly', image: '/images/palm1.jpg' },

  { id: 10, name: 'Peace Lily', category: 'Indoor', emoji: '🕊️', desc: 'A flowering indoor plant that enhances décor while naturally purifying indoor air.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Peace Lily.jpg' },

  { id: 11, name: 'Croton Plant', category: 'Indoor', emoji: '🎨', desc: 'A colorful foliage plant that adds vibrant beauty and character to any space.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Prune Croton.jpg' },

  { id: 12, name: 'Rubber Plant', category: 'Indoor', emoji: '🌳', desc: 'A premium air-purifying indoor plant with glossy leaves and a sophisticated look.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Rubber plant.jpg' },

  { id: 13, name: 'Snake Plant', category: 'Indoor', emoji: '🐍', desc: 'A hardy low-maintenance indoor plant known for excellent air purification benefits.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/snake plant.jpg' },

  { id: 14, name: 'Spider Plant', category: 'Indoor', emoji: '🕸️', desc: 'A fast-growing indoor plant that improves air quality and brightens interiors.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Spider Plant.jpg' },

  { id: 15, name: 'ZZ Plant', category: 'Indoor', emoji: '✨', desc: 'A stylish low-light indoor plant with glossy foliage and exceptional durability.', care: 'Easy', light: 'Any', water: '10 Days', image: '/images/Zz plant.jpg' },


  /* Outdoor */

  { id: 16, name: 'Bird of Paradise', category: 'Outdoor', emoji: '🦜', desc: 'A stunning tropical landscape plant with vibrant flowers and lush foliage, perfect for premium garden designs.', care: 'Moderate', light: 'Full Sun', water: '2x Week', image: '/images/Bird of Paradise O.jpg' },

  { id: 17, name: 'Desert Rose', category: 'Outdoor', emoji: '🌹', desc: 'A beautiful flowering outdoor plant with colorful blooms, ideal for balconies, patios, and garden décor.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/bonsai.jpg' },

  { id: 18, name: 'Calathea Freddie', category: 'Outdoor', emoji: '🌿', desc: 'An attractive foliage plant with patterned leaves, perfect for tropical landscaping and decorative gardens.', care: 'Moderate', light: 'Partial Shade', water: '2x Week', image: '/images/calathea freddie.jpg' },

  { id: 19, name: 'Canary Island Date Palm', category: 'Outdoor', emoji: '🌴', desc: 'A majestic palm tree that adds elegance and a luxurious tropical feel to outdoor landscapes.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/Canariensis Palm Tree.jpg' },

  { id: 20, name: 'Elephant Ear Plant', category: 'Outdoor', emoji: '🍀', desc: 'A bold ornamental plant with giant leaves, perfect for creating lush tropical garden environments.', care: 'Moderate', light: 'Partial Shade', water: '3x Week', image: '/images/Colocasia.jpg' },

  { id: 21, name: 'Pygmy Date Palm', category: 'Outdoor', emoji: '🌴', desc: 'A compact palm ideal for patios, entrances, and modern landscape gardening projects.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/Dwergdadelpalm O.jpg' },

  { id: 22, name: 'Norfolk Island Pine', category: 'Outdoor', emoji: '🌲', desc: 'A graceful evergreen tree that enhances gardens with year-round greenery and natural beauty.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/O tree.jpg' },

  { id: 23, name: 'Croton Plant', category: 'Outdoor', emoji: '🎨', desc: 'A colorful ornamental shrub with vibrant foliage that adds contrast and visual appeal to landscapes.', care: 'Moderate', light: 'Bright Sun', water: '2x Week', image: '/images/O tree4.jpg' },

  { id: 24, name: 'White Bird of Paradise', category: 'Outdoor', emoji: '🤍', desc: 'A striking tropical plant with large leaves, perfect for resorts, villas, and premium landscaping.', care: 'Moderate', light: 'Full Sun', water: '2x Week', image: '/images/outdoor.jpg' },

  { id: 25, name: 'Traveller’s Palm', category: 'Outdoor', emoji: '🌴', desc: 'A dramatic fan-shaped tropical plant that creates a stunning focal point in large outdoor spaces.', care: 'Moderate', light: 'Full Sun', water: '2x Week', image: '/images/palm2.jpg' },




  /* Herbal */

  { id: 26, name: 'Tulsi', category: 'Herbal', emoji: '🪔', desc: 'A sacred medicinal herb known for boosting immunity and supporting overall wellness naturally.', care: 'Easy', light: 'Full Sun', water: 'Daily', image: '/images/tulsi.jpg' },

  { id: 27, name: 'Aloe Vera', category: 'Herbal', emoji: '🌵', desc: 'A popular healing plant used for skincare, burns, and natural home remedies.', care: 'Easy', light: 'Bright Light', water: 'Weekly', image: '/images/Aloe Vera.jpg' },

  { id: 28, name: 'Mint (Pudina)', category: 'Herbal', emoji: '🌿', desc: 'A refreshing aromatic herb widely used in teas, drinks, and culinary recipes.', care: 'Easy', light: 'Partial Sun', water: 'Daily', image: '/images/mint.jpg' },

  { id: 29, name: 'Lemongrass', category: 'Herbal', emoji: '🌾', desc: 'A fragrant medicinal herb perfect for herbal tea, wellness gardens, and natural remedies.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/lemongrass.jpg' },

  { id: 30, name: 'Curry Leaf Plant', category: 'Herbal', emoji: '🍛', desc: 'An essential culinary herb packed with nutrients and widely used in Indian cooking.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/curry leaves.jpg' },

  { id: 31, name: 'Ashwagandha', category: 'Herbal', emoji: '🌱', desc: 'A renowned Ayurvedic herb known for supporting stress relief and overall vitality.', care: 'Moderate', light: 'Full Sun', water: 'Weekly', image: '/images/Ashwagandha.jpg' },

  { id: 32, name: 'Brahmi', category: 'Herbal', emoji: '🧠', desc: 'A traditional medicinal herb valued for memory support and cognitive wellness.', care: 'Easy', light: 'Partial Shade', water: 'Regular', image: '/images/brahmi.jpg' },

  { id: 33, name: 'Ajwain Plant', category: 'Herbal', emoji: '🌿', desc: 'A medicinal herb with aromatic leaves commonly used for digestive and wellness benefits.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/ajwain.jpg' },



  /* Masale (Spices) */

  { id: 34, name: 'Black Pepper', category: 'Spices', emoji: '⚫', desc: 'A climbing spice plant that produces flavorful black peppercorns for culinary and medicinal use.', care: 'Moderate', light: 'Partial Sun', water: '2x Week', image: '/images/black pepper.jpg' },

  { id: 35, name: 'Cardamom', category: 'Spices', emoji: '🌿', desc: 'A premium aromatic spice plant known for its fragrant pods and culinary value.', care: 'Moderate', light: 'Partial Shade', water: 'Regular', image: '/images/elaychi.jpg' },

  { id: 36, name: 'Cinnamon', category: 'Spices', emoji: '🪵', desc: 'A valuable spice tree grown for its aromatic bark used in cooking and wellness products.', care: 'Moderate', light: 'Full Sun', water: 'Weekly', image: '/images/Cinnamon.jpg' },

  { id: 37, name: 'Clove Plant', category: 'Spices', emoji: '🌸', desc: 'An evergreen spice plant producing fragrant flower buds widely used in traditional cooking.', care: 'Moderate', light: 'Full Sun', water: 'Regular', image: '/images/clove trees.jpg' },

  { id: 38, name: 'Chilli Plant', category: 'Spices', emoji: '🌶️', desc: 'A popular spice plant grown for its flavorful chilli peppers used in cooking.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/chilli.jpg' },

  { id: 39, name: 'Ginger', category: 'Spices', emoji: '🫚', desc: 'A popular spice plant grown for its flavorful roots used in cooking and herbal remedies.', care: 'Easy', light: 'Partial Shade', water: '2x Week', image: '/images/ginger.jpg' },

  { id: 40, name: 'Bay Leaf', category: 'Spices', emoji: '🍃', desc: 'An aromatic spice plant whose leaves add rich flavor to soups, curries, and rice dishes.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/Bay leaf.jpg' },



  /* Fruit Plants */

  { id: 41, name: 'Mango Tree', category: 'Fruit', emoji: '🥭', desc: 'A popular fruit tree that produces sweet, juicy mangoes and thrives in tropical gardens.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/mango.jpg' },

  { id: 42, name: 'Guava Plant', category: 'Fruit', emoji: '🍈', desc: 'A fast-growing fruit plant known for its nutritious fruits and easy maintenance.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/Guava Tree.jpg' },

  { id: 43, name: 'Lemon Plant', category: 'Fruit', emoji: '🍋', desc: 'A compact citrus plant that produces fresh lemons perfect for home gardens and patios.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/Limbu.jpg' },

  { id: 44, name: 'Pomegranate Plant', category: 'Fruit', emoji: '❤️', desc: 'A beautiful fruit-bearing plant known for its antioxidant-rich and delicious fruits.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/pomegranates.jpg' },

  { id: 45, name: 'Chikoo', category: 'Fruit', emoji: '🤎', desc: 'A hardy fruit tree producing sweet and nutritious chikoo fruits for home gardens.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/chiku.jpg' },

  { id: 46, name: 'Custard Apple', category: 'Fruit', emoji: '🍏', desc: 'A tropical fruit plant known for its creamy, sweet fruits and easy cultivation.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/sitafal.jpg' },




  /* Flowering Plants */

  { id: 47, name: 'Golden Champa (Son Champa)', category: 'Flowering', emoji: '🌼', desc: 'A fragrant flowering tree with golden-yellow blooms, perfect for gardens and landscape decoration.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/chafa.jpg' },

  { id: 48, name: 'Rajnigandha (Tuberose)', category: 'Flowering', emoji: '🤍', desc: 'A highly fragrant flowering plant known for elegant white blooms and long-lasting flowers.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/flower1.jpg' },

  { id: 49, name: 'Rose Plant', category: 'Flowering', emoji: '🌹', desc: 'A classic flowering plant with colorful blooms that add beauty and fragrance to any garden.', care: 'Moderate', light: 'Full Sun', water: '2x Week', image: '/images/flower2.jpg' },

  { id: 50, name: 'Rain Lily', category: 'Flowering', emoji: '🌸', desc: 'A charming flowering plant that produces vibrant blooms after rainfall and seasonal showers.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/flower3.jpg' },

  { id: 51, name: 'Amaryllis Lily', category: 'Flowering', emoji: '🌺', desc: 'A stunning flowering plant with large trumpet-shaped blooms ideal for outdoor landscapes.', care: 'Easy', light: 'Bright Sun', water: 'Weekly', image: '/images/flower4.jpg' },

  { id: 52, name: 'Parijat (Night Jasmine)', category: 'Flowering', emoji: '🌙', desc: 'A fragrant flowering plant famous for its white flowers and soothing evening fragrance.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/flower5.jpg' },

  { id: 53, name: 'Mogra (Arabian Jasmine)', category: 'Flowering', emoji: '🤍', desc: 'A popular flowering plant with fragrant white blooms, perfect for gardens and balconies.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/flowers.jpg' },

  { id: 54, name: 'Plumeria (Frangipani)', category: 'Flowering', emoji: '🌺', desc: 'A tropical flowering plant with colorful blooms that enhance outdoor spaces beautifully.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/O tree1.jpg' },

  { id: 55, name: 'Oleander', category: 'Flowering', emoji: '💗', desc: 'A hardy flowering shrub with vibrant blooms, ideal for landscaping and garden borders.', care: 'Easy', light: 'Full Sun', water: 'Weekly', image: '/images/O tree2.jpg' },

  { id: 56, name: 'Ixora', category: 'Flowering', emoji: '❤️', desc: 'A colorful flowering shrub with dense flower clusters, perfect for hedges and gardens.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/O tree3.jpg' },

  { id: 57, name: 'Peace Lily', category: 'Flowering', emoji: '🕊️', desc: 'An elegant flowering plant and natural air purifier, ideal for homes and offices.', care: 'Easy', light: 'Indirect Light', water: 'Weekly', image: '/images/Peace Lily.jpg' },

  { id: 58, name: 'Jasmine (Mogra)', category: 'Flowering', emoji: '🌼', desc: 'A fragrant flowering plant loved for its beautiful white flowers and soothing aroma.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/mogara.jpg' },


  /* Hanging Plants */

  { id: 59, name: 'Clematis Vine', category: 'Climbers & Hanging', emoji: '🌺', desc: 'A beautiful flowering climber with elegant blooms, perfect for walls, fences, and garden trellises.', care: 'Moderate', light: 'Full Sun', water: '2x Week', image: '/images/climbing1.jpg' },

  { id: 60, name: 'String of Pearls', category: 'Hanging', emoji: '🫧', desc: 'A unique trailing succulent with pearl-like leaves, ideal for hanging baskets and balconies.', care: 'Easy', light: 'Bright Light', water: 'Weekly', image: '/images/hanging.jpg' },

  { id: 61, name: 'Spider Plant', category: 'Hanging', emoji: '🕸️', desc: 'A low-maintenance air-purifying plant perfect for indoor and outdoor hanging planters.', care: 'Easy', light: 'Indirect Light', water: 'Weekly', image: '/images/hanging1.jpg' },

  { id: 62, name: 'English Ivy', category: 'Hanging', emoji: '🍃', desc: 'A fast-growing trailing plant that adds lush greenery to hanging baskets and vertical gardens.', care: 'Easy', light: 'Bright Light', water: 'Weekly', image: '/images/hanging2.jpg' },

  { id: 63, name: 'Heartleaf Philodendron', category: 'Hanging', emoji: '💚', desc: 'A popular trailing plant with heart-shaped leaves, ideal for home décor and hanging pots.', care: 'Easy', light: 'Indirect Light', water: 'Weekly', image: '/images/hanging3.jpg' },

  { id: 64, name: 'Variegated Ivy', category: 'Hanging', emoji: '🌿', desc: 'An attractive hanging plant with variegated foliage that brightens balconies and living spaces.', care: 'Easy', light: 'Bright Light', water: 'Weekly', image: '/images/hanging4.jpg' },

  { id: 65, name: 'Trailing Jade', category: 'Hanging', emoji: '💎', desc: 'A compact trailing succulent perfect for decorative hanging baskets and modern gardens.', care: 'Easy', light: 'Bright Sun', water: 'Weekly', image: '/images/hanging5.jpg' },

  { id: 66, name: 'Petunia Hanging Basket', category: 'Hanging Flowering', emoji: '🌸', desc: 'A colorful flowering hanging plant that creates vibrant displays for balconies and patios.', care: 'Easy', light: 'Full Sun', water: '2x Week', image: '/images/hanging6.jpg' },


];

const FILTERS = ['All',
  'Indoor',
  'Outdoor',
  'Herbal',
  'Spices',
  'Fruit',
  'Flowering',
  'Hanging',
];
const CATEGORY_COLORS = {
  Indoor: '#D1C4E9',              // Soft Green
  Outdoor: '#D7CCC8',             // Fresh Garden Green
  Herbal: '#AED581',              // Herbal Lime Green

  Spices: '#D7B899',              // Cinnamon Beige
  Fruit: '#FFCC80',               // Mango Peach
  Flowering: '#F8BBD0',           // Blossom Pink
  Hanging: '#B2EBF2',             // Sky Mint Blue

};
const CARE_COLORS = { Easy: '#2e7d32', Moderate: '#4caf50' };

/* ─── Single Plant Card ─── */
const PlantCollectionCard = ({ plant, idx }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const color = CATEGORY_COLORS[plant.category] || '#2e7d32';

  return (
    <div
      ref={ref}
      className={`pc-card ${visible ? 'pc-card--visible' : ''}`}
      style={{
        '--card-color': color,
        '--card-delay': `${(idx % 6) * 0.07}s`
      }}
    >
      {/* Background Image & Overlay */}
      <div className="pc-card-bg-img" style={{ backgroundImage: `url('${plant.image}')` }} />
      <div className="pc-card-overlay" />

      {/* Glow orb */}
      <div className="pc-card-glow" />

      {/* Card Content (z-index 1) */}
      <div className="pc-card-content" style={{ position: 'relative', zIndex: 1 }}>
        {/* Top: category label */}
        <div className="pc-card-top">
          <span className="pc-card-category" style={{ background: `${color}cc`, color: '#fff' }}>
            {plant.category}
          </span>
        </div>


        {/* Name & desc */}
        <h3 className="pc-card-name">{plant.name}</h3>
        <p className="pc-card-desc">{plant.desc}</p>

        {/* Meta chips */}
        <div className="pc-card-meta">
          <div className="pc-meta-chip">
            <span>Care: </span>
            <strong style={{ color: CARE_COLORS[plant.care] || '#fff' }}>{plant.care}</strong>
          </div>
          <div className="pc-meta-chip">
            <span>{plant.light}</span>
          </div>
          <div className="pc-meta-chip">
            <span>{plant.water}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="pc-card-footer">
          <Link to="/contact" className="pc-card-btn">
            Enquire
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
        </div>
      </div>

      {/* Border shimmer on hover */}
      <div className="pc-card-border" />
    </div>
  );
};

/* ─── Main Page ─── */
const PlantsCollectionPage = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [animating, setAnimating] = useState(false);
  const [displayed, setDisplayed] = useState(plants);
  const [shuffledAll, setShuffledAll] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Plant Collection — Nisarg Nursery';

    // Create a shuffled array for the 'All' filter
    const shuffled = [...plants].sort(() => Math.random() - 0.5);
    setShuffledAll(shuffled);
    setDisplayed(shuffled);
  }, []);

  const handleFilter = (f) => {
    if (f === activeFilter) return;
    setAnimating(true);
    setTimeout(() => {
      setActiveFilter(f);
      setDisplayed(f === 'All' ? shuffledAll : plants.filter(p => p.category === f));
      setAnimating(false);
    }, 280);
  };



  const filteredPlants = displayed.filter((plant) =>
    plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.desc.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === 'All' ? plants.length : plants.filter(p => p.category === f).length;
    return acc;
  }, {});

  return (
    <div className="pc-wrapper">
      {/* ════ HERO ════ */}
      <section className="pc-hero">

        <PlantBackground />
        <div className="pc-hero-bg-blur" />

        <div className="pc-hero-content">
          <div className="pc-hero-tag-minimal">
            BREATHE LIFE INTO YOUR SPACE
          </div>
          <h1 className="pc-hero-title">
            Discover Beautiful <br />

            <span className="gold-text">
              Indoor & Outdoor
            </span>
            <br />

            Plants for Every <br />
            Corner of Your Home
          </h1>
          <p className="pc-hero-desc">
            From indoor greens to outdoor blooms...<br />
            shop plants, pots, and care tools delivered with care.
          </p>

          <div className="pc-hero-actions-custom">
            <Link to="/products" className="pc-btn-primary">Shop Now</Link>
            <button className="pc-btn-play" onClick={() => setIsVideoOpen(true)}>
              <PlayCircle className="play-icon" />
              <span>
                <strong>Play Video</strong>
                <small>Download works</small>
              </span>
            </button>
          </div>

          <div className="pc-hero-join-text">
            Join 1000+ plant lovers growing their dream spaces.
          </div>
        </div>

        <div className="pc-right-gallery">

          <div className="pc-card-main">
            <img src="/images/hanging4.jpg" alt="Hanging Plant" />
          </div>

          <div className="pc-card-small">
            <img src="/images/snake plant.jpg" alt="Snake Plant" />
          </div>


        </div>



        <div className="pc-hero-stats-cards">
          <div className="pc-stat-glass-card">
            <span className="stat-label">Varieties</span>
            <h2 className="stat-value">1,900+</h2>
            <span className="stat-sub">Available Plant Variety</span>
          </div>
          <div className="pc-stat-glass-card">
            <span className="stat-label">Happy</span>
            <h2 className="stat-value">1,000+</h2>
            <span className="stat-sub">Happy Clients Subscribed</span>
          </div>
          <div className="pc-stat-glass-card">
            <span className="stat-label">Availability</span>
            <h2 className="stat-value">520+</h2>
            <span className="stat-sub">Local Stores</span>
          </div>
          <div className="pc-stat-glass-card">
            <span className="stat-label">Rating</span>
            <h2 className="stat-value">4.9 ★</h2>
            <span className="stat-sub">On Top Customers</span>
          </div>
        </div>
      </section>

      {/* ── FILTER BAR ── */}
      <div className="pc-filter-bar">
        <div className="pc-filter-inner">

          <span className="pc-filter-label">
            Filter by:
          </span>

          <div className="pc-search-box">
            <input
              type="text"
              placeholder="Search plants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pc-search-input"
            />
          </div>

          <div className="pc-filters">
            {FILTERS.map(f => (
              <button
                key={f}
                className={`pc-filter-btn ${activeFilter === f ? 'active' : ''}`}
                onClick={() => handleFilter(f)}
                style={activeFilter === f && f !== 'All'
                  ? { '--btn-color': CATEGORY_COLORS[f] }
                  : {}}
              >
                {f}
                <span className="pc-filter-count">{counts[f]}</span>
              </button>
            ))}
          </div>
        </div>
      </div>



      {/* ── RESULTS LABEL ── */}
      <div className="pc-results-bar">
        <span>
          Showing <strong>{filteredPlants.length}</strong> plants
          {activeFilter !== 'All' && <> in <strong>{activeFilter}</strong></>}
        </span>
      </div>

      {/* ── GRID ── */}
      <section className={`pc-grid-section ${animating ? 'pc-grid--exit' : 'pc-grid--enter'}`}>
        <div className="pc-grid">
          {filteredPlants.map((plant, idx) => (
            <PlantCollectionCard key={plant.id} plant={plant} idx={idx} />
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pc-cta">
        <div className="pc-cta-glow" />
        <span className="pc-cta-icon">🌱</span>
        <h2 className="pc-cta-title">
          Looking for a Specific <span className="gold-text">
            Rare Plant?
          </span>
        </h2>
        <p className="pc-cta-desc">

          We source rare, exotic, and large-scale plants on request for landscape gardening. Speak with our certified horticulturists today!

        </p>
        <div className="pc-cta-btns">
          <Link to="/contact" className="pc-btn-primary">
            Contact Our Experts
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="pc-btn-outline">
            View Our Services
          </Link>
        </div>
      </section>

      {/* ── Video Modal ── */}
      {isVideoOpen && (
        <div className="pc-video-modal" onClick={() => setIsVideoOpen(false)}>
          <div className="pc-video-modal-content" onClick={e => e.stopPropagation()}>
            <button className="pc-video-close" onClick={() => setIsVideoOpen(false)}>×</button>
            <video
              src="/images/video3.mp4"
              className="pc-modal-video"
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PlantsCollectionPage;
